import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { FieldOrigin, InputType, IVisibleProp } from 'config'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Section } from 'stories/components'
import { fillObject, openAuditLog } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { BorrowerSeperator } from '../config'
import { defaultInputs } from './constants'
import { LinkedEntitySelect } from './LinkedEntitySelect'
import { additionalLayerInputsValidate } from './logic'

export const AdditionalLayerInputs = ({
  borrower,
  borrowerSeperator,
  onUpdate,
}: {
  borrower: Record<string, any>
  borrowerSeperator: BorrowerSeperator
  onUpdate: Function
}) => {
  const [additionalLayerInputs, setAdditionalLayerInputs] = useState<Record<string, InputType>[]>([])
  const [fieldValueChanged, setFieldValueChanged] = useState(false)
  const borrowerInputs = defaultInputs()
  const { layerCount, isLayeredEntity } = borrower

  const additionalLayerFields: Record<string, IVisibleProp> = useSelector(
    (state: any) =>
      state.globalConfigReducer[GlobalConfigType.LoanFields].properties?.[FieldOrigin.BorrowerInformation]
        ?.additionalInputs?.additionalLayer || {},
  )
  const additionalLayerFieldsKey = JSON.stringify(additionalLayerFields)

  const buildLayerInputs = (nextBorrower: Record<string, any>) => {
    const validatedLayers = additionalLayerInputsValidate(borrowerSeperator, nextBorrower).fields

    return validatedLayers.map((layerInputs, index) => ({
      ...fillObject(
        Object.keys(layerInputs),
        (key: string) =>
          ({
            ...borrowerInputs[key],
            ...layerInputs[key],
            history: true,
          } as InputType),
      ),
      linkedEntity: {
        inputType: 'text',
        value: nextBorrower.additionalLayer?.[index]?.linkedEntity || '',
      } as InputType,
    }))
  }

  useEffect(() => {
    setAdditionalLayerInputs(buildLayerInputs(borrower))
  }, [layerCount, additionalLayerFieldsKey, borrower.additionalLayer])

  if (!isLayeredEntity || layerCount == 0) return null

  const showHistory = (layerIdx: number, key: string) => {
    const input = additionalLayerInputs?.[layerIdx]?.[key]
    const fieldKey = `additionalLayer[${layerIdx}][${key}]`
    const options = {
      table: 'Borrower',
      field: `${borrowerSeperator}-${fieldKey}`,
      keys: {
        field: fieldKey,
        borrowerSeperator,
      },
      renderValue: input?.options,
    }
    openAuditLog(options)
  }

  const onChange = (layerIdx: number, key: string, value: any) => {
    setFieldValueChanged(true)
    setAdditionalLayerInputs((prev) => {
      const newInputs = cloneDeep(prev)
      newInputs[layerIdx][key].value = value
      return newInputs
    })
  }

  const onLinkedEntityChange = (layerIdx: number, value: string) => {
    setAdditionalLayerInputs((prev) => {
      const newInputs = cloneDeep(prev)
      newInputs[layerIdx]['linkedEntity'].value = value
      const values = newInputs.map((layerInputs) =>
        fillObject(Object.keys(layerInputs), (k: string) => layerInputs[k].value),
      )
      onUpdate(values)
      return newInputs
    })
  }

  const onBlur = () => {
    if (!fieldValueChanged) return
    setFieldValueChanged(false)
    const values = additionalLayerInputs.map((inputs) =>
      fillObject(Object.keys(inputs), (key: string) => inputs[key].value),
    )
    setAdditionalLayerInputs(buildLayerInputs({ ...borrower, additionalLayer: values }))
    onUpdate(values)
  }

  const renderInput = (layerIdx: number, key: string) => {
    const input = additionalLayerInputs?.[layerIdx]?.[key]

    if (!input) return null

    if (key == 'entityTitle') {
      const entityTitles: Record<string, string> = {
        '-1': `Default: ${borrower.entityTitle}`,
      }
      borrower.additionalLayer.forEach((layer: Record<string, any>, index: number) => {
        if (index == layerIdx) return
        entityTitles[`${index}`] = `Layer ${index + 1}: ${layer.entityTitle || ''}`
      })
      input.additionalElements = (
        <LinkedEntitySelect
          entityTitles={entityTitles}
          value={additionalLayerInputs[layerIdx]?.linkedEntity?.value || ''}
          onChange={(value) => onLinkedEntityChange(layerIdx, value)}
        />
      )
    }

    return (
      <div className={''} key={`AdditionalLayer-${layerIdx}-${key}`}>
        <RenderInput
          input={input as any}
          Key={`layer-${layerIdx}-${key}`}
          onChange={(_: any, value: any) => onChange(layerIdx, key, value)}
          onBlur={() => onBlur()}
          showHistory={() => showHistory(layerIdx, key)}
        />
      </div>
    )
  }

  return (
    <div className="col-span-full">
      <p className="text-lg font-bold">Additional Layer</p>

      {Array.from({ length: layerCount }).map((_, index) => (
        <div key={`AdditionalLayer-${index}`}>
          <Section title={`Entity Layer ${index + 1}`} className="mb-3" />
          <div className="grid gap-4 md:grid-cols-2 grid-cols-1 mb-3">
            {Object.keys(additionalLayerFields).map((key) =>
              additionalLayerInputs[index]?.[key]?.visible === false ? null : renderInput(index, key),
            )}
          </div>
        </div>
      ))}
    </div>
  )
}
